import * as React from "react";
import { ShareDialog } from "@/components/share/ShareDialog";
import type { ShareAccess, ShareRole } from "@/components/share/types";
import { SnapshotCollection, SnapshotCollectionPermission } from "@/modules/snapshots/snapshotCollection.types";
import {
  useCreateSnapshotCollectionPermissionMutation,
  useDeleteSnapshotCollectionPermissionMutation,
  useUpdateSnapshotCollectionAccessMutation,
} from "@/modules/snapshots/snapshotCollection.api";
import { useSnapshotPermission } from "@/modules/snapshots/hooks/useSnapshotPermission";
import { useAuthenticatedUser } from "@/hooks/use-authenticated-user";

interface Props {
  snapshotCollection: SnapshotCollection;
  setIsOpen: (isOpen: boolean) => void;
  isOpen: boolean;
}

export const SnapshotCollectionShareModal: React.FC<Props> = ({ isOpen, setIsOpen, snapshotCollection }) => {
  const isReadOnly = !useSnapshotPermission(snapshotCollection);
  const authenticatedUser = useAuthenticatedUser();
  const [createPermission] = useCreateSnapshotCollectionPermissionMutation();
  const [deletePermission] = useDeleteSnapshotCollectionPermissionMutation();
  const [updateAccess] = useUpdateSnapshotCollectionAccessMutation();
  const { id } = snapshotCollection;

  const handleShare = React.useCallback(
    async (users: string[], role: ShareRole) => {
      await createPermission({ id, role, users }).unwrap();
    },
    [createPermission, id],
  );

  const handleRemovePermission = React.useCallback(
    async (permission: SnapshotCollectionPermission) => {
      await deletePermission({ id: permission.snapshot_collection_id, permissionId: permission.id }).unwrap();
    },
    [deletePermission],
  );

  const handleAccessChange = React.useCallback(
    async (access: ShareAccess) => {
      await updateAccess({ id, access }).unwrap();
    },
    [updateAccess, id],
  );

  return (
    <ShareDialog
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={<>Share Snapshot collection &#34;{snapshotCollection.name}&#34;</>}
      isReadOnly={isReadOnly}
      permissions={snapshotCollection.permissions}
      access={snapshotCollection.access}
      shareSuccessMessage="Snapshot collection shared successfully."
      onShare={handleShare}
      onRemovePermission={handleRemovePermission}
      canRemovePermission={permission => permission.user_id !== authenticatedUser.id}
      onAccessChange={handleAccessChange}
    />
  );
};
